// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React from 'react';

const section = { marginBottom: '1.5rem' };
const h2Style = { fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1F2937' };
const pStyle = { fontSize: '0.95rem', lineHeight: 1.7, color: '#374151', marginBottom: '0.5rem' };
const listStyle = { paddingLeft: '1.5rem', marginBottom: '0.75rem' };
const liStyle = { fontSize: '0.95rem', lineHeight: 1.7, color: '#374151', marginBottom: '0.25rem' };

const TermsPage = () => (
  <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto', fontFamily: 'system-ui' }}>
    <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#111827' }}>Terms of Service</h1>
    <p style={{ fontWeight: 600, color: '#374151' }}>Miskulin Technologies Inc.</p>
    <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '2rem' }}>Last updated: February 20, 2026 | Version 2.0</p>

    <div style={section}>
      <h2 style={h2Style}>1. Acceptance of Terms</h2>
      <p style={pStyle}>By accessing or using any application operated by Miskulin Technologies ("MT", "we", "us"), you agree to be bound by these Terms of Service. If you do not agree to these Terms, do not use the service.</p>
    </div>

    <div style={section}>
      <h2 style={h2Style}>2. Service Tiers & Data Handling</h2>
      <p style={pStyle}>MT offers multiple service tiers. Your tier determines how your data is handled:</p>

      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1F2937', marginTop: '1rem' }}>2.1 Demo Trial (1-Week)</h3>
      <ul style={listStyle}>
        <li style={liStyle}>Demo trials provide full access to the premium (Scale) tier for evaluation purposes.</li>
        <li style={liStyle}>During the demo, your data is stored in our systems solely to provide the service.</li>
        <li style={liStyle}><strong>If you do not subscribe after the demo period:</strong> All data associated with your account will be permanently deleted within 48 hours of demo expiry.</li>
        <li style={liStyle}>You will receive a Proof of Deletion Receipt confirming the removal of all your data.</li>
        <li style={liStyle}>No copies, backups, or derivatives of your demo data will be retained by MT.</li>
        <li style={liStyle}>You may request early deletion at any time during the demo period.</li>
      </ul>

      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1F2937', marginTop: '1rem' }}>2.2 Free Tier</h3>
      <ul style={listStyle}>
        <li style={liStyle}>By using the free tier, you grant Miskulin Technologies a non-exclusive, worldwide license to use, process, and analyze your data for the following purposes:</li>
        <li style={liStyle}>&mdash; Platform improvement and feature development</li>
        <li style={liStyle}>&mdash; Anonymized analytics and aggregate industry insights</li>
        <li style={liStyle}>&mdash; AI model training (data is anonymized before use)</li>
        <li style={liStyle}>Your data will NOT be sold to third parties or shared with competitors.</li>
        <li style={liStyle}>You may upgrade to a paid tier at any time to gain full data privacy protections.</li>
        <li style={liStyle}>You may request account deletion at any time. Data will be scrubbed within 30 days.</li>
      </ul>

      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1F2937', marginTop: '1rem' }}>2.3 Growth Tier (Paid)</h3>
      <ul style={listStyle}>
        <li style={liStyle}>Your data is 100% owned by you. MT will not use, analyze, or access your data except for technical support (with your explicit consent) and system maintenance.</li>
        <li style={liStyle}>Data is encrypted at rest and in transit.</li>
        <li style={liStyle}>You may export your data at any time.</li>
        <li style={liStyle}>Account deletion requests are processed within 7 days.</li>
      </ul>

      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1F2937', marginTop: '1rem' }}>2.4 Scale Tier (Paid — Premium)</h3>
      <ul style={listStyle}>
        <li style={liStyle}>All Growth tier protections apply, plus:</li>
        <li style={liStyle}>Priority deletion requests processed within 24 hours.</li>
        <li style={liStyle}>Annual data audit report available on request.</li>
        <li style={liStyle}>Custom data retention schedules available.</li>
      </ul>
    </div>

    <div style={section}>
      <h2 style={h2Style}>3. Use of Service</h2>
      <p style={pStyle}>You agree to use the service only for lawful purposes and in accordance with these Terms. You are responsible for maintaining the confidentiality of your account credentials. You must not attempt to access other users' data, reverse-engineer the platform, or circumvent security measures.</p>
    </div>

    <div style={section}>
      <h2 style={h2Style}>4. Intellectual Property</h2>
      <p style={pStyle}>All content, features, functionality, and underlying technology are owned by Miskulin Technologies and are protected by international copyright, trademark, patent, and other intellectual property laws. The MT Platform, all associated applications, and the Forge Standard are proprietary to Miskulin Technologies.</p>
    </div>

    <div style={section}>
      <h2 style={h2Style}>5. Account Termination</h2>
      <p style={pStyle}>We may suspend or terminate your account if you violate these Terms. Upon termination, your data will be handled according to your service tier's retention policy (see Section 2). You may request account deletion at any time by contacting us.</p>
    </div>

    <div style={section}>
      <h2 style={h2Style}>6. Limitation of Liability</h2>
      <p style={pStyle}>Miskulin Technologies shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the service. Our total liability shall not exceed the amount you paid to MT in the 12 months preceding the claim.</p>
    </div>

    <div style={section}>
      <h2 style={h2Style}>7. Governing Law</h2>
      <p style={pStyle}>These Terms are governed by the laws of the Province of Alberta, Canada. Any disputes shall be resolved in the courts of Alberta, Canada.</p>
    </div>

    <div style={section}>
      <h2 style={h2Style}>8. Changes to Terms</h2>
      <p style={pStyle}>We may update these Terms from time to time. Continued use of the service after changes constitutes acceptance of the revised Terms. Material changes will be communicated via email or in-app notification.</p>
    </div>

    <div style={{ ...section, borderTop: '1px solid #E5E7EB', paddingTop: '1rem' }}>
      <h2 style={h2Style}>9. Contact</h2>
      <p style={pStyle}>Questions about these terms: <a href="mailto:investor@miskulintechnologies.com" style={{ color: '#2563EB' }}>investor@miskulintechnologies.com</a></p>
      <p style={pStyle}>Miskulin Technologies Inc.<br />121 Rainbow Falls Bay, Chestermere, AB T1X 0S5, Canada</p>
    </div>
  </div>
);

export default TermsPage;
